import endpoints from '@app/src/api/endpoints'
import { FetchKey } from '@app/src/api/fetchHooks'
import { useDeleteResource } from '@app/src/api/updateHooks'
import FilterDisplayChip from '@app/src/components/FilterDisplayChip'
import { useAmplitude } from '@app/src/context/AmplitudeContext'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import type RequestError from '@app/src/errors/RequestError'
import useErrorNotification from '@app/src/hooks/errorNotification'
import { useMenuState } from '@app/src/hooks/mui-hooks'
import SimpleRiskChip from '@app/src/pages/Assessments/AssessmentCompanyPage/SimpleRiskChip'
import type { FilterGroup, FilterValue, Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { AnswerFlagRule } from '@app/src/types/answerFlags'
import { formatRuleValues } from '@app/src/utils/answerFlagUtils'
import { AmplitudeTrackingEvents, NotificationSeverity } from '@app/src/wf-constants'
import { Delete, Edit } from '@mui/icons-material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import type { TypographyProps } from '@mui/material'
import {
  Box,
  CircularProgress,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useConfirm } from 'material-ui-confirm'
import React from 'react'
import { useIntl } from 'react-intl'

interface AnswerFlagTableRowProps {
  row: AnswerFlagRule
  onEdit: (answerFlagRule: AnswerFlagRule) => void
}

interface FilterChipData {
  name: string
  value: FilterValue
  operator: Operators
}

function extractFilterChipData(providerFilters: FilterGroup[] | undefined): FilterChipData[] {
  if (!providerFilters) return []

  // NOTE: Currently only extracts the first filter from each FilterGroup.
  // If FilterGroups contain multiple filters, only the first one will be displayed.
  // TODO: Verify with API integration whether multiple filters per group need to be handled.
  const result: FilterChipData[] = []
  for (const filter of providerFilters) {
    const firstFilter = filter.filters?.[0]
    if (firstFilter?.value) {
      result.push({
        name: filter.name,
        value: firstFilter.value as FilterValue,
        operator: firstFilter.operator,
      })
    }
  }
  return result
}

function AnswerFlagTableRow({ row, onEdit }: AnswerFlagTableRowProps): React.ReactElement {
  const { formatMessage } = useIntl()
  const [isMenuOpen, menuAnchorElement, openMenu, closeMenu] = useMenuState()
  const queryClient = useQueryClient()
  const { showErrorNotification } = useErrorNotification()
  const { showSnackbar } = useSnackbar()
  const confirm = useConfirm()
  const { trackEvent } = useAmplitude()

  const { mutateAsync: deleteRule, isPending: isDeleting } = useDeleteResource({
    options: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: [FetchKey.AnswerFlagRuleCollection] })
        trackEvent({
          name: AmplitudeTrackingEvents.Analyze.SupplierRisk.ConfiguredFlags,
          eventProps: {
            action: 'delete',
            ruleId: row.id,
            ruleTitle: row.description,
          },
        })
        showSnackbar({
          severity: NotificationSeverity.success,
          message: formatMessage({ id: 'answerFlagRules.notifications.deleted' }),
        })
        closeMenu()
      },
      onError: (error: RequestError) => {
        showErrorNotification({ requestError: error })
      },
    },
  })

  const filters = extractFilterChipData(row.providerFilters)

  const handleEdit = () => {
    closeMenu()
    onEdit(row)
  }

  const performDelete = async () => {
    await deleteRule({ url: endpoints.deleteAnswerFlagRule(row.id) })
  }

  const handleDelete = () => {
    confirm({
      content: formatMessage({ id: 'answerFlagRules.deleteConfirmation.content' }),
      confirmationText: formatMessage({ id: 'answerFlagRules.deleteConfirmation.confirmButton' }),
    })
      .then(performDelete)
      .catch(() => {
        closeMenu()
      })
  }

  return (
    <TableRow>
      <TableCell sx={{ maxWidth: 450, verticalAlign: 'top' }}>
        <EllipsisText text={row.description} />
        <EllipsisText text={row.question?.questionText} variant="body2" color="text.disabled" sx={{ mt: 0.5 }} />
        <EllipsisText text={formatRuleValues(row)} variant="body2" color="text.disabled" />
      </TableCell>

      <TableCell sx={{ verticalAlign: 'top' }}>
        {filters.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {filters.slice(0, 2).map((filter, idx) => (
              <FilterDisplayChip
                key={`${filter.name}-${idx}`}
                filterName={filter.name}
                filterValue={filter.value}
                operator={filter.operator}
              />
            ))}
            {filters.length > 2 && (
              <Typography variant="caption" color="text.disabled" sx={{ alignSelf: 'center', ml: 0.5 }}>
                +{filters.length - 2} more
              </Typography>
            )}
          </Box>
        ) : (
          <Typography variant="body1" color="text.disabled">
            {formatMessage({ id: 'general.notAdded' })}
          </Typography>
        )}
      </TableCell>

      <TableCell align="right" sx={{ verticalAlign: 'top' }}>
        <SimpleRiskChip riskStatus={row.riskStatus} />
      </TableCell>

      <TableCell align="right" sx={{ verticalAlign: 'top' }}>
        <IconButton onClick={openMenu} size="small">
          <MoreVertIcon />
        </IconButton>
        <Menu anchorEl={menuAnchorElement} open={isMenuOpen} onClose={closeMenu}>
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <Edit />
            </ListItemIcon>
            {formatMessage({ id: 'general.edit' })}
          </MenuItem>
          <MenuItem onClick={handleDelete} disabled={isDeleting}>
            <ListItemIcon>{isDeleting ? <CircularProgress size={20} /> : <Delete />}</ListItemIcon>
            {formatMessage({ id: 'general.delete' })}
          </MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  )
}

interface EllipsisTextProps extends TypographyProps {
  text: string | undefined | null
  showTooltip?: boolean
}

function EllipsisText({ text, showTooltip = true, sx, ...props }: EllipsisTextProps): React.ReactElement | null {
  if (!text) return null

  const typography = (
    <Typography
      {...props}
      sx={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'block',
        ...sx,
      }}
    >
      {text}
    </Typography>
  )

  if (showTooltip) {
    return (
      <Tooltip title={text} arrow>
        {typography}
      </Tooltip>
    )
  }

  return typography
}

export default AnswerFlagTableRow
